import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./historyPage.module.css";
import { lotteryService, formatCurrency, formatShortDate, formatApiError } from "../../../../../services/api";
import { useAuth } from "../../../../../context/authContext";
import { Spinner, EmptyState, ErrorBanner, Badge } from "../../../../../components/ui";
import TicketModal from "./TicketModal";
import { Reveal } from "../../../../../components/motion";

export default function HistoryPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all"); // all, pending, won, lost
  // Apuesta (de GET /lottery/bets/) cuyo ticket se está viendo; null = modal cerrado.
  const [ticketBet, setTicketBet] = useState(null);

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      setLoading(false);
      setBets([]);
      return;
    }

    const fetchBets = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await lotteryService.getBets();
        if (result.ok) {
          const list = Array.isArray(result.data) ? result.data : result.data?.results || [];
          setBets(list);
        } else {
          setError(formatApiError(result.data) || "Error al cargar el historial");
        }
      } catch (err) {
        setError("Error de conexión");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchBets();
  }, [isAuthenticated, authLoading]);

  const getStatusLabel = (status) => {
    const s = status?.toUpperCase();
    switch (s) {
      case "PENDING": return "Pendiente";
      case "PLAYED": return "Jugada";
      case "WON": return "Ganador";
      case "LOST": return "No ganó";
      case "PAID": return "Pagado";
      case "CANCELLED": return "Cancelada";
      default: return status;
    }
  };

  const filteredBets = bets.filter(bet => {
    if (filter === "all") return true;
    const st = bet.status?.toUpperCase();
    if (filter === "pending") return st === "PENDING" || st === "PLAYED";
    if (filter === "won") return st === "WON" || st === "PAID";
    return st === filter.toUpperCase();
  });

  if (!authLoading && !isAuthenticated) {
    return (
      <div className={styles.container}>
        <EmptyState
          title="Inicia sesión"
          description="Para ver tu historial de apuestas necesitas una cuenta activa."
          actionLabel="Ir a iniciar sesión"
          onAction={() => navigate("/auth", { state: { from: "/historial" } })}
          icon="🔐"
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner label="Cargando historial…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <ErrorBanner message={error} />
      </div>
    );
  }

  const statusVariant = (status) => {
    const s = status?.toUpperCase();
    if (s === 'WON' || s === 'PAID') return 'success';
    if (s === 'LOST') return 'error';
    if (s === 'PENDING' || s === 'PLAYED') return 'pending';
    return 'default';
  };

  return (
    <div className={styles.container}>
      <div className={styles.filters}>
        <button 
          className={`${styles.filterBtn} ${filter === "all" ? styles.active : ""}`}
          onClick={() => setFilter("all")}
        >
          Todas
        </button>
        <button 
          className={`${styles.filterBtn} ${filter === "pending" ? styles.active : ""}`}
          onClick={() => setFilter("pending")}
        >
          Pendientes
        </button>
        <button 
          className={`${styles.filterBtn} ${filter === "won" ? styles.active : ""}`}
          onClick={() => setFilter("won")}
        >
          Ganadas
        </button>
        <button 
          className={`${styles.filterBtn} ${filter === "lost" ? styles.active : ""}`}
          onClick={() => setFilter("lost")}
        >
          No ganó
        </button>
      </div>

      {filteredBets.length === 0 ? (
        <EmptyState
          title={filter === "all" ? "Sin apuestas aún" : "Sin resultados"}
          description="¡Comienza a jugar y prueba tu suerte!"
          actionLabel="Jugar ahora"
          onAction={() => navigate("/apuestas")}
          icon="🎫"
        />
      ) : (
        <div className={styles.betsList}>
          {filteredBets.map((bet, i) => (
            <Reveal key={bet.id} delay={Math.min(i, 6) * 0.04} className={styles.card}>
              {/* HEADER */}
              <div className={styles.header}>
                <div className={styles.headerLeft}>
                  {bet.lottery_logo && (
                    <img 
                      src={bet.lottery_logo} 
                      alt={bet.lottery_name} 
                      className={styles.lotteryLogo}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div className={styles.headerInfo}>
                    <span className={styles.lotteryName}>{bet.lottery_name || "Lotería"}</span>
                    <span className={styles.date}>
                      Sorteo #{bet.sorteo || bet.draw_number} • {bet.transaction_date || formatShortDate(bet.created_at)}
                    </span>
                  </div>
                </div>
                <Badge variant={statusVariant(bet.status)}>
                  {getStatusLabel(bet.status)}
                </Badge>
              </div>

              {/* BODY */}
              <div className={styles.body}>
                <div className={styles.betInfo}>
                  <div className={styles.numberBox}>
                    <span className={styles.label}>Número</span>
                    <span className={styles.value}>{bet.number}</span>
                  </div>
                  <div className={styles.numberBox}>
                    <span className={styles.label}>Serie</span>
                    <span className={styles.value}>{bet.series}</span>
                  </div>
                  <div className={styles.numberBox}>
                    <span className={styles.label}>Fracciones</span>
                    <span className={styles.value}>{bet.fractions}/{bet.total_fractions}</span>
                  </div>
                </div>

                {/* Info adicional */}
                <div className={styles.extraInfo}>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Distribuidor:</span>
                    <span className={styles.infoValue}>{bet.distributor || "N/A"}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Ubicación:</span>
                    <span className={styles.infoValue}>{bet.location || "N/A"}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Ref:</span>
                    <span className={styles.infoValue}>{bet.transaction_reference || bet.id?.slice(0, 8)}</span>
                  </div>
                </div>

                {bet.status?.toUpperCase() === "WON" && bet.prize_amount && (
                  <div className={styles.prizeRow}>
                    <span>🏆 Premio:</span>
                    <strong className={styles.prizeAmount}>{formatCurrency(bet.prize_amount)}</strong>
                  </div>
                )}
              </div>

              {/* FOOTER */}
              <div className={styles.footer}>
                <div className={styles.footerItem}>
                  <span>Precio fracción</span>
                  <strong>{formatCurrency(bet.fraction_price)}</strong>
                </div>
                <div className={styles.footerItem}>
                  <span>Total pagado</span>
                  <strong>{formatCurrency(bet.amount)}</strong>
                </div>
              </div>

              <div className={styles.cardActions}>
                <button
                  type="button"
                  className={styles.viewTicketBtn}
                  onClick={() => setTicketBet(bet)}
                >
                  Ver ticket
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      )}

      <TicketModal
        isOpen={ticketBet != null}
        bet={ticketBet}
        onClose={() => setTicketBet(null)}
      />
    </div>
  );
}
